import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { GenOperationSummary } from 'sdk/docs-types';
import { nanoid } from 'shared/utils/nanoid';
import { usePrerenderTrigger } from '~/hooks/use-prerender';
import { useCurrentSection } from '~/hooks/use-scroll-spy';
import { operationsByTagQueryOptions, tagDetailsQueryOptions, tagsQueryOptions } from '~/modules/docs/query';
import { SidebarMenu } from '~/modules/ui/sidebar';
import { queryClient } from '~/query/query-client';
import { CollapsibleTagItem } from './collapsible-tag-item';
import { OperationItem } from './operation-item';

const itemKey = (op: GenOperationSummary) => op.hash;
const renderItem = (op: GenOperationSummary, _index: number, isActive: boolean) => <OperationItem operation={op} isActive={isActive} />;

interface OperationsSidebarProps {
  activeTag?: string;
}

export function OperationsSidebar({ activeTag }: OperationsSidebarProps) {
  const [layoutId] = useState(() => nanoid());
  const { prerender } = usePrerenderTrigger('operations');

  const { data: operationsByTag } = useSuspenseQuery(operationsByTagQueryOptions);
  const { data: allTags } = useSuspenseQuery(tagsQueryOptions);
  const tags = allTags.filter((t) => t.count > 0);
  const hash = useCurrentSection();

  return (
    <SidebarMenu className="gap-1 p-0 pt-1 pb-4">
      {tags.map((tag) => {
        const isExpanded = activeTag === tag.name;
        // Only pass items for expanded tag to avoid mounting all operations upfront
        const tagOperations = isExpanded ? (operationsByTag[tag.name] ?? []) : [];
        const isAtTag = hash === `tag/${tag.name}`;
        const isActive = isAtTag || hash?.startsWith(`tag/${tag.name}/`);
        const activeOperationIndex = tagOperations.findIndex((op) => op.hash === hash);

        return (
          <CollapsibleTagItem
            type="operations"
            key={tag.name}
            tag={tag}
            items={tagOperations}
            isExpanded={isExpanded}
            layoutId={layoutId}
            isActive={isActive}
            isAtTag={isAtTag}
            activeItemIndex={activeOperationIndex}
            renderItem={renderItem}
            itemKey={itemKey}
            onPrerender={() => {
              void queryClient.query(tagDetailsQueryOptions(tag.name)).catch(() => {});
              prerender(tag.name);
            }}
          />
        );
      })}
    </SidebarMenu>
  );
}
